import React, { useState } from 'react';
import { User } from '../types';

interface UserSearchPanelProps {
  currentUser: User;
  onFavoriteToggle: (userId: string) => void;
  onCallRequest: (targetUserId: string) => void;
  isFavorite: (userId: string) => boolean;
  canCallRequest: (targetUserId: string) => boolean;
}

const UserSearchPanel: React.FC<UserSearchPanelProps> = ({
  currentUser,
  onFavoriteToggle,
  onCallRequest,
  isFavorite,
  canCallRequest
}) => {
  const [searchUserCode, setSearchUserCode] = useState('');
  const [searchedUser, setSearchedUser] = useState<User | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const API_URL = 'https://voicechatapi.kasaneko.com';

  const handleSearch = async () => {
    if (!searchUserCode.trim()) {
      setSearchError('ユーザーIDを入力してください');
      return;
    }

    setSearchLoading(true);
    setSearchError(null);
    setSearchedUser(null);

    try {
      const response = await fetch(`${API_URL}/api/users/search/by-code/${encodeURIComponent(searchUserCode.trim())}`);
      const data = await response.json();

      if (data.success && data.user) {
        // 自分自身は検索結果に含めない
        if (data.user.userId === currentUser.userId) {
          setSearchError('自分自身は検索できません');
          return;
        }

        setSearchedUser({
          userId: data.user.userId,
          userCode: data.user.userCode,
          username: data.user.username,
          profileImage: data.user.profileImage,
          gender: data.user.gender,
          hobbies: data.user.hobbies,
          bio: data.user.bio,
          position: data.user.position || { x: 0, y: 0, z: 0 },
          isOnline: data.user.isOnline,
          avatar: data.user.avatar
        });
      } else {
        setSearchError(data.error || 'ユーザー情報が取得できませんでした');
      }
    } catch (error) {
      console.error('ユーザー検索エラー:', error);
      setSearchError('ユーザー情報が取得できませんでした');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleFavoriteClick = () => {
    if (searchedUser) {
      onFavoriteToggle(searchedUser.userId);
    }
  };

  const handleCallRequestClick = () => {
    if (searchedUser) {
      onCallRequest(searchedUser.userId);
    }
  };

  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      padding: '20px',
      overflow: 'auto',
      background: 'linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 50%, #dde9ff 100%)'
    }}>
      <h2 style={{
        color: '#333',
        fontSize: '24px',
        fontWeight: 'bold',
        marginBottom: '20px',
        textAlign: 'center'
      }}>
        ユーザー検索
      </h2>

      {/* 検索フォーム */}
      <div style={{
        display: 'flex',
        gap: '10px',
        marginBottom: '20px'
      }}>
        <input
          type="text"
          value={searchUserCode}
          onChange={(e) => setSearchUserCode(e.target.value.toUpperCase())}
          placeholder="ユーザーID（例：VC12345678）"
          style={{
            flex: 1,
            padding: '12px',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '8px',
            fontSize: '16px',
            background: 'rgba(255, 255, 255, 0.9)',
            color: '#333'
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleSearch();
            }
          }}
        />
        <button
          onClick={handleSearch}
          disabled={searchLoading}
          style={{
            padding: '12px 24px',
            background: searchLoading ? '#ccc' : '#667eea',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: searchLoading ? 'not-allowed' : 'pointer'
          }}
        >
          {searchLoading ? '検索中...' : '検索'}
        </button>
      </div>

      {/* エラー表示 */}
      {searchError && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.9)',
          color: '#c62828',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          {searchError}
        </div>
      )}

      {/* 検索結果 */}
      {searchedUser && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '15px',
          padding: '20px',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
          position: 'relative'
        }}>
          {/* お気に入りボタン（右上） */}
          <button
            onClick={handleFavoriteClick}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '32px',
              padding: '5px',
              lineHeight: '1',
              zIndex: 10
            }}
            title={isFavorite(searchedUser.userId) ? 'お気に入り解除' : 'お気に入り追加'}
          >
            {isFavorite(searchedUser.userId) ? '❤️' : '🤍'}
          </button>

          {/* ユーザーアイコン */}
          <div style={{
            textAlign: 'center',
            marginBottom: '20px'
          }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: searchedUser.profileImage
                ? `url(${searchedUser.profileImage}) center/cover`
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              margin: '0 auto 15px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '48px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
              border: '3px solid #667eea'
            }}>
              {!searchedUser.profileImage && '👤'}
            </div>
            
            <h3 style={{
              margin: '0 0 5px 0',
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#333'
            }}>
              {searchedUser.username}
            </h3>
            
            {searchedUser.userCode && (
              <div style={{
                fontSize: '14px',
                color: '#666',
                marginBottom: '15px'
              }}>
                ID: {searchedUser.userCode}
              </div>
            )}
          </div>

          {/* プロフィール情報 */}
          <div style={{
            background: '#f5f5f5',
            borderRadius: '10px',
            padding: '15px',
            marginBottom: '20px'
          }}>
            {searchedUser.gender && (
              <div style={{ marginBottom: '10px', fontSize: '14px' }}>
                <strong>性別:</strong> {searchedUser.gender}
              </div>
            )}
            {searchedUser.hobbies && (
              <div style={{ marginBottom: '10px', fontSize: '14px' }}>
                <strong>趣味:</strong> {searchedUser.hobbies}
              </div>
            )}
            {searchedUser.bio && (
              <div style={{ marginBottom: '10px', fontSize: '14px' }}>
                <strong>自己紹介:</strong> {searchedUser.bio}
              </div>
            )}
            {!searchedUser.gender && !searchedUser.hobbies && !searchedUser.bio && (
              <div style={{ textAlign: 'center', color: '#999', fontSize: '14px' }}>
                まだ設定されていません
              </div>
            )}
          </div>

          {/* 通話依頼ボタン（プロフィール内の下） */}
          <button
            onClick={handleCallRequestClick}
            disabled={!canCallRequest(searchedUser.userId)}
            style={{
              width: '100%',
              padding: '15px',
              background: canCallRequest(searchedUser.userId) ? '#4CAF50' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: canCallRequest(searchedUser.userId) ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <span>⭐</span>
            {canCallRequest(searchedUser.userId) ? '通話依頼' : '通話依頼（利用不可）'}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserSearchPanel;

